import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store';
import { updateHabit, fetchHabits } from '../store/slices/habitsSlice';
import { fetchWeeklyView } from '../store/slices/recordsSlice';
import { useTranslation } from '../hooks/useTranslation';
import '../styles/Modal.css';

interface EditHabitModalProps {
  isOpen: boolean;
  onClose: () => void;
  habit: any;
}

const EditHabitModal: React.FC<EditHabitModalProps> = ({ isOpen, onClose, habit }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  
  const [habitData, setHabitData] = useState({
    name: '',
    description: '',
    color: '#4CAF50',
    icon: '📝',
    isArchived: 0,
    repeatDays: {
      mon: true, tue: true, wed: true, thu: true,
      fri: true, sat: true, sun: true
    },
    goalType: 'continuous' as 'continuous' | 'weekly',
    weeklyGoal: 3,
  });

  useEffect(() => {
    if (habit) {
      setHabitData({
        name: habit.name,
        description: habit.description || '',
        color: habit.color,
        icon: habit.icon || '📝',
        isArchived: habit.isArchived || 0,
        repeatDays: habit.repeatDays || {
          mon: true, tue: true, wed: true, thu: true,
          fri: true, sat: true, sun: true
        },
        goalType: habit.goalType || 'continuous',
        weeklyGoal: habit.weeklyGoal || 3,
      });
    }
  }, [habit]);

  const colors = [
    '#4CAF50', '#2196F3', '#FF9800', '#E91E63',
    '#9C27B0', '#00BCD4', '#FFC107', '#795548'
  ];

  const emojis = [
    '📝', '💪', '📚', '🏃', '💧', '🧘', '💊', '🎯',
    '🎨', '🎸', '💻', '📖', '✍️', '🏋️', '🚶', '🍎'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!habitData.name.trim() || !habit) return;

    const habitToUpdate = {
      id: habit.id!,
      data: {
        name: habitData.name,
        description: habitData.description,
        color: habitData.color,
        icon: habitData.icon,
        isArchived: habitData.isArchived,
        repeatDays: habitData.repeatDays,
        goalType: habitData.goalType,
        weeklyGoal: habitData.goalType === 'weekly' ? habitData.weeklyGoal : undefined,
      },
    };

    try {
      const result = await dispatch(updateHabit(habitToUpdate));
      
      if (result.type.endsWith('/fulfilled')) {
        dispatch(fetchHabits());
        dispatch(fetchWeeklyView());
        onClose();
      } else {
        alert(t('habit.updateFailed'));
      }
    } catch (error) {
      console.error('[EditHabitModal] Error updating habit:', error);
      alert(t('common.error'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('habit.edit')}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form className="habit-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="edit-habit-name">📝 {t('habit.name')}</label>
            <input
              id="edit-habit-name"
              type="text"
              value={habitData.name}
              onChange={(e) => setHabitData({...habitData, name: e.target.value})}
              placeholder={t('habit.namePlaceholder')}
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-habit-description">📄 {t('habit.description')}</label>
            <textarea
              id="edit-habit-description"
              value={habitData.description}
              onChange={(e) => setHabitData({...habitData, description: e.target.value})}
              placeholder={t('habit.descriptionPlaceholder')}
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>🎨 {t('habit.color')}</label>
            <div className="color-selector">
              {colors.map(color => (
                <button
                  key={color}
                  type="button"
                  className={`color-option ${habitData.color === color ? 'selected' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setHabitData({...habitData, color})}
                />
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>😊 {t('habit.icon')}</label>
            <div className="emoji-selector">
              {emojis.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  className={`emoji-option ${habitData.icon === emoji ? 'selected' : ''}`}
                  onClick={() => setHabitData({...habitData, icon: emoji})}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>📅 {t('habit.repeatDays')}</label>
            <div className="weekday-selector">
              {Object.entries(habitData.repeatDays).map(([day, checked]) => (
                <label key={day} className={`weekday-option ${checked ? 'checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => setHabitData({
                      ...habitData,
                      repeatDays: {
                        ...habitData.repeatDays,
                        [day]: e.target.checked
                      }
                    })}
                  />
                  <span>{t(`weekday.${day}`)}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="edit-goal-type">🎯 {t('habit.goalType')}</label>
            <select
              id="edit-goal-type"
              value={habitData.goalType}
              onChange={(e) => setHabitData({
                ...habitData,
                goalType: e.target.value as 'continuous' | 'weekly'
              })}
            >
              <option value="continuous">{t('habit.continuousGoal')}</option>
              <option value="weekly">{t('habit.weeklyGoal')}</option>
            </select>
          </div>

          {habitData.goalType === 'weekly' && (
            <div className="form-group">
              <label htmlFor="edit-weekly-goal">{t('habit.weeklyGoalCount')}</label>
              <div className="weekday-goal-input">
                <select
                  id="edit-weekly-goal"
                  value={habitData.weeklyGoal}
                  onChange={(e) => setHabitData({
                    ...habitData,
                    weeklyGoal: parseInt(e.target.value) || 1
                  })}
                >
                  {[1,2,3,4,5,6,7].map((n) => (
                    <option key={n} value={n}>{`${n} ${t('habit.timesPerWeek')}`}</option>
                  ))}
                </select>
              </div>
              <div className="goal-help" style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginTop: '5px',
                lineHeight: '1.4'
              }}>
                {t('habit.weeklyGoalExample').replace('{count}', habitData.weeklyGoal.toString())}
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="edit-habit-archived">
              <input
                id="edit-habit-archived"
                type="checkbox"
                checked={habitData.isArchived === 1}
                onChange={(e) => setHabitData({...habitData, isArchived: e.target.checked ? 1 : 0})}
              />
              <span style={{ marginLeft: '8px' }}>{t('habit.archive')}</span>
            </label>
          </div>
          
          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
            >
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditHabitModal;
